/**
 * Assistance Request detail (AR.5) — a modal showing one request with its full
 * lifecycle (IMS Logistics resource tracking) and the operator controls to advance
 * it: a single forward-only "advance" action (with an optional person/notes field),
 * a Cancel action, and — #328c / R4 — a "Request assistance" affordance that opens the
 * two-way inter-ops information-sharing thread scoped to THIS request (context_type
 * 'assistance_request'), replacing the retired live Forward-to-PEOC control. The legacy
 * /forward webhook route persists (deprecated) so a past forward stays a truthful record;
 * the "Forwarded" provenance block below still describes those past acts on legacy rows.
 *
 * Opening logs `view_assistance_request`; transitions are audited server-side
 * (`update_assistance_request_status` / `cancel_assistance_request`); a request-assistance
 * send is audited as `send_interops_message`. Escape-to-close mirrors SitrepDetailPanel.
 */
import { useEffect, useState } from 'react';
import type { AssistanceRequest, AssistanceStatus, Incident } from '@tsr/shared-types';
import { ResponseLevelBadge } from './ResponseLevelBadge';
import { THREAT_LEVEL_META } from './styles/responseLevel';
import { LifecycleStepper, type LifecycleStep, type StepperUrgency } from './components/LifecycleStepper';
import { ASSIST_STATUS_LABEL, ASSIST_STATUS_ORDER } from './AssistQueueList';
import {
  acknowledgeAssistanceRequest,
  fetchIncidents,
  linkAssistanceRequestToIncident,
  taskAgencyFromAssistance,
  updateAssistanceRequestStatus,
} from './api';
import { DestinationPicker } from './DestinationPicker';
import { AgencyPicker } from './AgencyPicker';
import { DeskWatchControl } from './components/DeskWatchControl';
import { InteropsThreadPanel } from './InteropsThreadPanel';
import { REQUEST_ASSISTANCE_LABEL } from './interopsVocabulary';
import { getSessionId, logAction } from './lib/operatorActions';
import { showToast } from './lib/toast';
import { selectOnCop } from './selectionBridge';
import { resolveConnectedAlertSelection } from './lib/alertAssistLink';

/** Operator-facing label for the Urgent Alert an assist is linked to. */
const CONNECTED_ALERT_LABEL: Record<string, string> = {
  high_risk_hotspot: 'fused wildfire hotspot',
  cap_alert: 'CAP public-safety alert',
};

/** The single forward step from each non-terminal status. */
const NEXT_STATUS: Partial<Record<AssistanceStatus, AssistanceStatus>> = {
  submitted: 'triaged',
  triaged: 'routed',
  routed: 'assigned',
  assigned: 'in_progress',
  in_progress: 'fulfilled',
  fulfilled: 'closed',
};

/** Label + the optional person/notes field carried by each advance.
 *  NB (Ruling 3): the `triaged`, `routed`, and `assigned` advances are now REAL
 *  decisions, not free-text. Triage renders an RL 1–4 selector (sends
 *  `response_level`); route renders the DestinationPicker (sends
 *  `routed_destination_id`); ASSIGN (Phase 3b-1) carries NO field — advancing binds a
 *  real resource server-side (the linked resource_request), surfaced read-only below.
 *  The remaining `field`/`placeholder` entries are the backward-compatible fallback
 *  for the steps the footer doesn't special-case. */
const ADVANCE_META: Record<
  string,
  { label: string; field?: 'triaged_by' | 'routed_to' | 'assigned_to' | 'fulfillment_notes'; placeholder?: string }
> = {
  triaged: { label: 'Triage', field: 'triaged_by', placeholder: 'Triaged by (optional)' },
  routed: { label: 'Route', field: 'routed_to', placeholder: 'Routed to (optional)' },
  assigned: { label: 'Assign' },
  in_progress: { label: 'Mark in progress' },
  fulfilled: { label: 'Fulfil', field: 'fulfillment_notes', placeholder: 'Fulfillment notes (optional)' },
  closed: { label: 'Close' },
};

const RESPONSE_LEVELS = [1, 2, 3, 4] as const;

/** Triage affordance (Ruling 3 / Phase 3a) — pick the machine Threat Level (triage
 *  severity, #613). An assistance request is not an owned incident with an IC-assessed
 *  §5 Response Level, so this operator-assigned 1–4 is the Threat axis. Styled to match
 *  ResponseLevelBadge (the same shared palette): the selected level is filled, the rest
 *  are tinted. Colour is never load-bearing alone — each button carries the "N · <name>"
 *  text. */
function TriageLevelSelector({
  value,
  onChange,
  disabled,
}: {
  value: number | undefined;
  onChange: (level: number) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="triage-rl-selector">
      <span className="text-[11px] uppercase tracking-wide text-slate-400">Threat Level</span>
      {RESPONSE_LEVELS.map((lvl) => {
        const s = THREAT_LEVEL_META[lvl];
        const selected = value === lvl;
        return (
          <button
            key={lvl}
            type="button"
            data-testid={`triage-rl-${lvl}`}
            aria-pressed={selected}
            disabled={disabled}
            onClick={() => onChange(lvl)}
            className="text-[10px] px-1.5 py-0.5 rounded font-medium border disabled:opacity-50"
            style={
              selected
                ? { backgroundColor: s.color, color: '#0b1120', borderColor: s.color }
                : { backgroundColor: `${s.color}33`, color: s.color, borderColor: `${s.color}66` }
            }
            title={s.criteria}
          >
            {lvl} · {s.name}
          </button>
        );
      })}
    </div>
  );
}

const URGENCY_CLS: Record<string, string> = {
  immediate: 'bg-rose-600/30 border-rose-500/60 text-rose-200',
  urgent: 'bg-amber-600/30 border-amber-500/60 text-amber-200',
  routine: 'bg-slate-700/60 border-slate-600 text-slate-300',
};

function fmt(iso: string | null): string {
  if (!iso) return '—';
  return iso.length >= 16 ? `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC` : iso;
}

/** Compact timestamp for the stepper (no "UTC" suffix to keep it short). */
function fmtShort(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return iso.length >= 16 ? iso.slice(5, 16).replace('T', ' ') : iso;
}

/** Build the §3B.5 stepper steps from a request's status + timestamps. The lifecycle
 *  order + labels are the shared assist constants (AssistQueueList) so this modal
 *  stepper and the Coordination → Assist worklist stepper never drift. */
function buildSteps(r: AssistanceRequest): LifecycleStep[] {
  const stamps: Record<string, { ts: string | null; detail?: string | null }> = {
    submitted: { ts: r.created_at },
    triaged: { ts: r.triaged_at, detail: r.triaged_by },
    routed: { ts: r.routed_at, detail: r.routed_to },
    // Assign binds a real resource (Phase 3b-1): show the bound RR ref on the step,
    // falling back to any legacy free-text `assigned_to` only when no RR is linked.
    assigned: {
      ts: r.assigned_at,
      detail: r.assigned_resource_request
        ? `${r.assigned_resource_request.request_number} · ${r.assigned_resource_request.request_status.replace(/_/g, ' ')}`
        : r.assigned_to,
    },
    in_progress: { ts: r.assigned_at ? r.assigned_at : null },
    fulfilled: { ts: r.fulfilled_at, detail: r.fulfillment_notes },
    closed: { ts: r.closed_at },
  };
  const cancelled = r.status === 'cancelled';
  const currentIdx = ASSIST_STATUS_ORDER.indexOf(r.status);
  return ASSIST_STATUS_ORDER.map((s, i) => {
    const stamp = stamps[s];
    const status: LifecycleStep['status'] = cancelled
      ? stamp?.ts
        ? 'complete'
        : 'future'
      : i < currentIdx
        ? 'complete'
        : i === currentIdx
          ? 'current'
          : 'future';
    return {
      label: ASSIST_STATUS_LABEL[s],
      status,
      timestamp: fmtShort(stamp?.ts),
      detail: stamp?.detail ?? null,
    };
  });
}

export function AssistanceRequestDetail({
  request,
  onClose,
  onUpdated,
}: {
  request: AssistanceRequest;
  onClose: () => void;
  onUpdated: (request: AssistanceRequest) => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  // #419 — the explicit (can't-triage-yet) acknowledge action's in-flight guard.
  const [acking, setAcking] = useState(false);
  const [fieldValue, setFieldValue] = useState('');
  // #328c / R4 — the "Request assistance" affordance toggles the inline inter-ops thread
  // (context_type 'assistance_request') for THIS request, replacing the retired forward CTA.
  const [showThread, setShowThread] = useState(false);
  // Triage SETS the RL (Ruling 3 / Phase 3a) — seeded with the submit-time derived
  // level so triage re-affirms (or overrides) a concrete value, never sends nothing.
  const [triageLevel, setTriageLevel] = useState<number | undefined>(request.response_level ?? undefined);
  // Route BINDS a destination (Ruling 3 / Phase 3a) — distinct from the forward picker.
  const [routeDestinationId, setRouteDestinationId] = useState<string | undefined>(undefined);
  // #379 — "Task agency": the dominant down-tasking control (replaces forward-to-self).
  const [taskAgencyId, setTaskAgencyId] = useState<string | undefined>(undefined);
  const [tasking, setTasking] = useState(false);
  // #425 LINK — attach this request to a tracked incident (closes the request→incident
  // loop). The picker lists open incidents; linking surfaces the incident # + down-tasks.
  const [openIncidents, setOpenIncidents] = useState<Incident[]>([]);
  const [linkIncidentId, setLinkIncidentId] = useState('');
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    logAction('view_assistance_request', { assistance_request_id: request.id, status: request.status });
  }, [request.id, request.status]);

  // #425 LINK — load open incidents for the picker (best-effort; the control hides if none).
  useEffect(() => {
    const ctl = new AbortController();
    void fetchIncidents({ status: 'open', limit: 100 }, ctl.signal)
      .then(setOpenIncidents)
      .catch(() => {
        /* picker is best-effort */
      });
    return () => ctl.abort();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Reset the per-step affordances whenever the status changes: clear the free-text
  // field, re-seed the triage RL selector from the current level, and clear the route
  // binding so the DestinationPicker re-auto-selects its default on the next step.
  useEffect(() => {
    setFieldValue('');
    setTriageLevel(request.response_level ?? undefined);
    setRouteDestinationId(undefined);
  }, [request.status, request.response_level]);

  const next = NEXT_STATUS[request.status];
  const isTerminal = request.status === 'closed' || request.status === 'cancelled';

  const transition = (newStatus: AssistanceStatus): void => {
    if (busy) return;
    setBusy(true);
    const meta = ADVANCE_META[newStatus];
    const body: Parameters<typeof updateAssistanceRequestStatus>[1] = {
      new_status: newStatus,
      session_id: getSessionId(),
    };
    if (newStatus === 'triaged') {
      // Triage SETS the Response Level (Ruling 3 / Phase 3a).
      if (triageLevel !== undefined) body.response_level = triageLevel;
    } else if (newStatus === 'routed') {
      // Route BINDS a real destination (Ruling 3 / Phase 3a).
      if (routeDestinationId) body.routed_destination_id = routeDestinationId;
    } else if (meta?.field && fieldValue.trim()) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (body as any)[meta.field] = fieldValue.trim();
    }
    void updateAssistanceRequestStatus(request.id, body)
      .then((updated) => {
        showToast(`Request → ${newStatus.replace(/_/g, ' ')}.`, { variant: 'success' });
        onUpdated(updated);
      })
      .catch(() => showToast('Could not update the request status.', { variant: 'error' }))
      .finally(() => setBusy(false));
  };

  // #379 — "Task agency": issue a down-task to the picked NON-SELF agency, carrying this
  // assistance request. The dominant down-tasking control — it replaced the old forward-to-self
  // default (#379); the later live Forward-to-PEOC affordance is now retired too (#328c / R4,
  // rewired onto the "Request assistance" inter-ops thread below).
  const taskAgency = (): void => {
    if (tasking || !taskAgencyId) return;
    setTasking(true);
    void taskAgencyFromAssistance(request.id, {
      target_agency_id: taskAgencyId,
      session_id: getSessionId(),
    })
      .then(() => {
        showToast('Tasked the agency — tracked in Coordination → Tasks.', { variant: 'success' });
        setTaskAgencyId(undefined);
      })
      .catch(() => showToast('Could not task the agency. Please retry.', { variant: 'error' }))
      .finally(() => setTasking(false));
  };

  // #425 LINK — attach (incidentId) or detach (null) this request to/from a tracked incident.
  const link = (incidentId: string | null): void => {
    if (linking) return;
    setLinking(true);
    void linkAssistanceRequestToIncident(request.id, incidentId, getSessionId())
      .then((updated) => {
        showToast(
          incidentId
            ? `Linked to ${updated.incident_number ?? 'incident'}.`
            : 'Unlinked from the incident.',
          { variant: incidentId ? 'success' : 'info' },
        );
        setLinkIncidentId('');
        onUpdated(updated);
      })
      .catch(() => showToast('Could not update the incident link.', { variant: 'error' }))
      .finally(() => setLinking(false));
  };

  // #419 closed-loop — stamp the requestor-facing receipt WITHOUT triaging (the
  // can't-triage-yet path). Orthogonal to the status machine; idempotent server-side.
  // Triage auto-stamps it, so this is only ever shown on a still-`submitted` request.
  const acknowledge = (): void => {
    if (acking || request.acknowledged_at) return;
    setAcking(true);
    void acknowledgeAssistanceRequest(request.id, getSessionId())
      .then((updated) => {
        showToast('Receipt acknowledged — the requestor can see it was received.', {
          variant: 'success',
        });
        onUpdated(updated);
      })
      .catch(() => showToast('Could not acknowledge the request.', { variant: 'error' }))
      .finally(() => setAcking(false));
  };

  const r = request;
  const advanceMeta = next ? ADVANCE_META[next] : undefined;

  // #185 — the Urgent Alert this request is connected to (same incident spine).
  // Rendered ONLY when a cross-linkable alert exists (contextual, no clutter).
  const connectedAlert = resolveConnectedAlertSelection({
    incident_link_type: r.incident_link_type,
    incident_link_id: r.incident_link_id,
    lat: r.geographic_scope?.lat ?? null,
    lon: r.geographic_scope?.lon ?? null,
  });
  const openConnectedAlert = (): void => {
    if (!connectedAlert) return;
    // No new audit verb (migration-free): selecting the alert downstream already
    // logs its own `view_*_detail` action via the COP one-selection handlers.
    selectOnCop(connectedAlert); // reuse the COP one-selection model + fly
    onClose();
  };

  // #426 — fly the COP to the request's own captured point (independent of any
  // connected alert). Reuses the selection bridge's pure point kind; closing the
  // modal lets the operator see the map move under it.
  const showOnMap = (): void => {
    const g = r.geographic_scope;
    if (!g) return;
    selectOnCop({ kind: 'assistance', id: r.id, lat: g.lat, lon: g.lon });
    onClose();
  };

  return (
    <div
      data-testid="assistance-detail-backdrop"
      onClick={onClose}
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-6"
    >
      <div
        data-testid="assistance-detail-modal"
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col w-full max-w-xl max-h-[88vh] rounded-lg border border-slate-700 bg-slate-900 text-slate-100 shadow-2xl"
      >
        <header className="flex items-start justify-between gap-3 px-5 py-3 border-b border-slate-700">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold tracking-tight truncate" data-testid="assistance-detail-title">
              {r.requestor_name}
            </h2>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">
                {r.requestor_type.replace(/_/g, ' ')}
              </span>
              <span
                className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${URGENCY_CLS[r.urgency] ?? ''}`}
              >
                {r.urgency}
              </span>
              {/* #613 — an assistance request is not an owned incident; its triage level is
                  the machine Threat axis (matches the TriageLevelSelector in this same modal). */}
              <ResponseLevelBadge level={r.response_level} axis="threat" />
              <span
                data-testid="assistance-detail-status"
                className="text-[10px] uppercase tracking-wide text-sky-300"
              >
                {r.status.replace(/_/g, ' ')}
              </span>
              {/* #419 closed-loop — the PEOC receipt, shown as a DISCRETE badge (NOT an
                  inline status stage). Once stamped (explicitly or auto-on-triage), the
                  badge replaces the button so a triaged row never carries a dead control. */}
              {r.acknowledged_at ? (
                <span
                  data-testid="assistance-detail-received-badge"
                  title={`Received by PEOC · ${fmt(r.acknowledged_at)}`}
                  className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-emerald-600/50 text-emerald-300"
                >
                  ✓ Received {fmtShort(r.acknowledged_at)}
                </span>
              ) : (
                r.status === 'submitted' && (
                  <button
                    type="button"
                    data-testid="assistance-acknowledge-btn"
                    onClick={acknowledge}
                    disabled={acking}
                    title="Stamp 'received' without triaging yet — the requestor sees it was acknowledged"
                    className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-slate-600 text-slate-300 hover:border-emerald-600/50 hover:text-emerald-300 disabled:opacity-50"
                  >
                    Acknowledge receipt
                  </button>
                )
              )}
              {/* W4c duty watch (#526) — a per-DESK "Watching" flag on this request file, an
                  ORTHOGONAL stamp (like ✓ Received): eyes-on WITHOUT claiming/owning it.
                  Non-exclusive; the object is the WORK ("this request"), never the requestor. */}
              <DeskWatchControl sourceType="assistance_request" sourceId={r.id} variant="chip" />
            </div>
          </div>
          <button
            type="button"
            data-testid="assistance-detail-close"
            onClick={onClose}
            className="shrink-0 text-slate-400 hover:text-slate-100 text-lg leading-none px-1"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3 text-xs text-slate-300">
          {r.incident_summary && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Linked incident</div>
              <div>{r.incident_summary}</div>
            </div>
          )}

          {/* #425 LINK — requestor-side read-back: the tracked incident this request was
              attached to ("your request is now INC-YYYY-NNNNN") + the down-tasks issued
              in its service, with live status. */}
          {r.incident_number && (
            <div data-testid="assistance-linked-incident">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Tracked incident</div>
              <div className="mt-1 inline-flex items-center gap-2 rounded border border-emerald-700/60 bg-emerald-900/20 px-2 py-1.5">
                <span className="text-[11px] font-mono font-medium text-emerald-200">
                  {r.incident_number}
                </span>
                <span className="text-[10px] text-emerald-400/80">your request is tracked here</span>
              </div>
            </div>
          )}

          {r.down_tasks && r.down_tasks.length > 0 && (
            <div data-testid="assistance-down-tasks">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Down-tasks issued</div>
              <ul className="mt-1 space-y-0.5">
                {r.down_tasks.map((t) => (
                  <li key={t.id} className="flex items-center justify-between gap-2 text-[11px]">
                    <span className="font-mono text-slate-200">
                      {t.task_number} → {t.target_agency_name ?? 'agency'}
                    </span>
                    <span className="uppercase tracking-wide text-slate-400">
                      {t.task_status.replace(/_/g, ' ')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {connectedAlert && (
            <div data-testid="connected-alert">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Connected alert</div>
              <button
                type="button"
                data-testid="connected-alert-btn"
                onClick={openConnectedAlert}
                className="mt-1 flex w-full items-center gap-2 rounded border border-rose-500/50 bg-rose-600/15 px-2 py-1.5 text-left text-rose-100 hover:bg-rose-600/25"
              >
                <span aria-hidden className="text-rose-300">
                  ◎
                </span>
                <span className="min-w-0 flex-1 truncate">
                  Open the {CONNECTED_ALERT_LABEL[r.incident_link_type ?? ''] ?? 'Urgent Alert'} on the map
                </span>
                <span aria-hidden className="text-rose-300">
                  →
                </span>
              </button>
            </div>
          )}

          {r.geographic_scope && (
            <div data-testid="assistance-location">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Location</div>
              <button
                type="button"
                data-testid="assistance-show-on-map"
                onClick={showOnMap}
                className="mt-1 flex w-full items-center gap-2 rounded border border-sky-500/50 bg-sky-600/15 px-2 py-1.5 text-left text-sky-100 hover:bg-sky-600/25"
              >
                <span aria-hidden className="text-sky-300">
                  ◎
                </span>
                <span className="min-w-0 flex-1 truncate">
                  Show on map — {r.geographic_scope.lat.toFixed(4)}, {r.geographic_scope.lon.toFixed(4)}
                </span>
                <span aria-hidden className="text-sky-300">
                  →
                </span>
              </button>
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Requested resources</div>
            <div>{r.requested_resources.categories.join(', ')}</div>
            {r.requested_resources.details && (
              <div className="text-slate-400">{r.requested_resources.details}</div>
            )}
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Narrative</div>
            <p className="whitespace-pre-wrap">{r.request_narrative}</p>
          </div>

          {r.requestor_contact && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Contact</div>
              <div>
                {[r.requestor_contact.name, r.requestor_contact.role, r.requestor_contact.email, r.requestor_contact.phone]
                  .filter(Boolean)
                  .join(' · ') || '—'}
              </div>
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">Lifecycle</div>
            <LifecycleStepper
              data-testid="assistance-lifecycle-stepper"
              steps={buildSteps(r)}
              urgency={r.urgency as StepperUrgency}
            />
            {r.status === 'cancelled' && (
              <div className="mt-2 text-[10px] uppercase tracking-wide text-rose-300">
                Cancelled · {fmt(r.closed_at)}
              </div>
            )}
            {r.fulfillment_notes && <div className="text-slate-400 mt-2">{r.fulfillment_notes}</div>}
          </div>

          {r.assigned_resource_request && (
            // Assign binds a REAL resource (Ruling 3 / §H.5): the originator sees the
            // bound resource_request — RR-YYYY-NNNNN · status — not a free-text name.
            // Compact read-only line in the ResourceRequestsSection visual language;
            // the full 7-state lifecycle stays managed in the incident thread.
            <div data-testid="assistance-assigned-resource">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Assigned resource</div>
              <div className="mt-1 inline-flex items-center gap-2 rounded border border-slate-700 bg-slate-800/40 px-2 py-1.5">
                <span className="text-[11px] font-medium text-slate-200">
                  {r.assigned_resource_request.request_number}
                </span>
                <span aria-hidden className="text-slate-500">·</span>
                <span className="text-[10px] uppercase tracking-wide text-slate-400">
                  {r.assigned_resource_request.request_status.replace(/_/g, ' ')}
                </span>
              </div>
            </div>
          )}

          {r.forwarded_to && (
            <div data-testid="assistance-forwarded">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Forwarded</div>
              <div>
                {fmt(r.forwarded_at)} → {r.forwarded_to}
                {r.forwarded_payload_id ? ` · delivery #${r.forwarded_payload_id.slice(0, 8)}` : ''}
              </div>
            </div>
          )}

          {/* #328c / R4 — the inter-ops information-sharing thread for THIS request, opened by the
              footer "Request assistance" button. Two-way + multi-destination; the composer defaults
              to the RFA kind (context_type 'assistance_request'). */}
          {showThread && (
            <div data-testid="assistance-interops-thread">
              <InteropsThreadPanel
                contextType="assistance_request"
                contextId={r.id}
                defaultKind="request_for_assistance"
              />
            </div>
          )}
        </div>

        <footer className="flex flex-col gap-2 px-5 py-3 border-t border-slate-700">
          {!isTerminal && next && (
            <div className="flex items-center gap-2 flex-wrap">
              {next === 'triaged' ? (
                // Triage SETS the Response Level (Ruling 3 / Phase 3a) — RL selector, not free-text.
                <TriageLevelSelector value={triageLevel} onChange={setTriageLevel} disabled={busy} />
              ) : next === 'routed' ? (
                // Route BINDS a real destination (Ruling 3 / Phase 3a) — the registry picker, not free-text.
                <DestinationPicker
                  value={routeDestinationId}
                  onChange={setRouteDestinationId}
                  disabled={busy}
                  label="Route to"
                  testid="assistance-route-destination-picker"
                  emptyHint="No delivery destinations configured — cannot route"
                />
              ) : (
                advanceMeta?.field && (
                  <input
                    data-testid="assistance-advance-field"
                    value={fieldValue}
                    onChange={(e) => setFieldValue(e.target.value)}
                    placeholder={advanceMeta.placeholder}
                    className="flex-1 min-w-[8rem] text-xs bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-slate-100 placeholder-slate-500"
                  />
                )
              )}
              <button
                type="button"
                data-testid="assistance-advance-btn"
                // Route cannot advance bound to nobody (§G gate): require a destination
                // selected before the route step can advance (empty/unreachable registry
                // → no auto-select → button stays disabled, hint explains why).
                disabled={busy || (next === 'routed' && !routeDestinationId)}
                onClick={() => transition(next)}
                className="text-xs py-1.5 px-3 rounded bg-emerald-600/90 hover:bg-emerald-600 border border-emerald-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
              >
                {advanceMeta?.label ?? 'Advance'} →
              </button>
            </div>
          )}
          {/* #379 — DOMINANT down-tasking control: task a NAMED, NON-SELF agency. Replaces
              the old forward-to-self default; the tasked agency acknowledges + works it. */}
          {!isTerminal && (
            <div className="flex items-center gap-2 flex-wrap">
              <AgencyPicker
                value={taskAgencyId}
                onChange={setTaskAgencyId}
                disabled={tasking}
                testid="assistance-agency-picker"
              />
              <button
                type="button"
                data-testid="assistance-task-agency-btn"
                disabled={tasking || !taskAgencyId}
                onClick={taskAgency}
                className="text-xs py-1.5 px-3 rounded bg-amber-600/90 hover:bg-amber-600 border border-amber-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
              >
                {tasking ? 'Tasking…' : 'Task agency'}
              </button>
            </div>
          )}
          {/* #425 LINK — attach this request to a tracked incident (closes the
              request→incident loop; the incident then shows this request + its tasks,
              and the read-back above shows the incident # to the requestor). */}
          {!isTerminal && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="assistance-link-incident-row">
              {r.incident_id ? (
                <>
                  <span className="text-[11px] text-slate-400">
                    Linked to{' '}
                    <span className="font-mono text-emerald-300">{r.incident_number ?? 'incident'}</span>
                  </span>
                  <button
                    type="button"
                    data-testid="assistance-unlink-incident-btn"
                    disabled={linking}
                    onClick={() => link(null)}
                    className="text-xs py-1.5 px-3 rounded border border-slate-600 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                  >
                    Unlink
                  </button>
                </>
              ) : openIncidents.length > 0 ? (
                <>
                  <select
                    value={linkIncidentId}
                    onChange={(e) => setLinkIncidentId(e.target.value)}
                    disabled={linking}
                    data-testid="assistance-link-incident-select"
                    aria-label="Link to incident"
                    className="flex-1 min-w-[10rem] text-xs bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-slate-100"
                  >
                    <option value="">Link to incident…</option>
                    {openIncidents.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.incident_number} · {i.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    data-testid="assistance-link-incident-btn"
                    disabled={linking || !linkIncidentId}
                    onClick={() => link(linkIncidentId)}
                    className="text-xs py-1.5 px-3 rounded bg-indigo-600/90 hover:bg-indigo-600 border border-indigo-500 text-white font-medium disabled:opacity-50"
                  >
                    {linking ? 'Linking…' : 'Link'}
                  </button>
                </>
              ) : null}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            {/* #328c / R4 — the "Request assistance" affordance replaces the retired live
                Forward-to-PEOC control: it opens the two-way inter-ops information-sharing thread
                for THIS request (context_type 'assistance_request'), where the operator picks the
                EOC / ministry / federal recipients and requests assistance in-app. */}
            <button
              type="button"
              data-testid="assistance-request-assistance-btn"
              aria-expanded={showThread}
              onClick={() => setShowThread((v) => !v)}
              className="text-xs py-1.5 px-3 rounded bg-sky-600/80 hover:bg-sky-600 border border-sky-500 text-white font-medium"
            >
              {REQUEST_ASSISTANCE_LABEL}
            </button>
            {!isTerminal && (
              <button
                type="button"
                data-testid="assistance-cancel-btn"
                disabled={busy}
                onClick={() => transition('cancelled')}
                className="text-xs py-1.5 px-3 rounded bg-rose-700/70 hover:bg-rose-700 border border-rose-600 text-rose-100 disabled:opacity-50"
              >
                Cancel request
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="ml-auto text-xs py-1.5 px-3 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300"
            >
              Close
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
